
class FacetFiltersForm extends HTMLElement {
  static COLLECTION_STATE_PARAM_PREFIXES = ['filter.', 'sort_by', 'page', 'q'];

  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
    viewMode();
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = window.location.search.slice(1);
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static hasDeferredDrawerFilters() {
    return Boolean(
      window.MyMaraDeferredFacets
      && typeof window.MyMaraDeferredFacets.isEnabled === 'function'
      && window.MyMaraDeferredFacets.isEnabled()
    );
  }

  static hideLoadingSpinners() {
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static removeCollectionStateParams(urlSearchParams) {
    Array.from(urlSearchParams.keys()).forEach((key) => {
      if (FacetFiltersForm.COLLECTION_STATE_PARAM_PREFIXES.some((prefix) => key === prefix || key.startsWith(prefix))) {
        urlSearchParams.delete(key);
      }
    });
  }

  static buildCollectionUrl(searchParams = window.location.search.slice(1), { includeSectionId = false } = {}) {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return null;

    const url = new URL(window.location.href);
    const nextSearchParams = new URLSearchParams(searchParams);

    FacetFiltersForm.removeCollectionStateParams(url.searchParams);

    const clearedKeys = new Set();
    nextSearchParams.forEach((value, key) => {
      if (!clearedKeys.has(key)) {
        url.searchParams.delete(key);
        clearedKeys.add(key);
      }
      url.searchParams.append(key, value);
    });

    if (includeSectionId) {
      url.searchParams.set('section_id', productGrid.dataset.id);
    } else {
      url.searchParams.delete('section_id');
    }

    url.searchParams.delete('drawer_section');
    url.searchParams.delete('pagination_section');
    return `${url.pathname}${url.search}`;
  }

  static initializeScrollAnimations(target = document) {
    if (typeof initializeScrollAnimationTrigger !== 'function' || !target) return;
    initializeScrollAnimationTrigger(target);
  }

  static parseResponse(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  // The section picks its variant from the request URL parsed out of content_for_header, which Shopify
  // sometimes reports for a different request (the filter drawer or an infinite-scroll page, which has no
  // filters). Fall back to a full page load then.
  static isFilterResponse(parsedResponse) {
    return Boolean(
      parsedResponse.getElementById('ProductGridContainer') && parsedResponse.getElementById('main-collection-filters')
    );
  }

  static loadFullPage(sectionUrl) {
    const url = FacetFiltersForm.buildCollectionUrl(new URL(sectionUrl, window.location.origin).search.slice(1));
    // renderPage has already pushed this URL into the history, so replace it instead of adding a duplicate
    if (url) window.location.replace(url);
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    if (updateURLHash) document.querySelector('custom-pagination')?.rememberPosition();
    document.querySelector('custom-pagination')?.suspend();
    FacetFiltersForm.searchParamsPrev = searchParams;
    const requestId = FacetFiltersForm.beginRequest();
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const countContainerMobile = document.getElementById('ProductCountMobile');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }
    if (countContainerMobile) {
      countContainerMobile.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = FacetFiltersForm.buildCollectionUrl(searchParams, { includeSectionId: true, sectionId: section.section });
      if (!url) return;
      const filterDataUrl = (element) => element.url === url;

      if (FacetFiltersForm.hasDeferredDrawerFilters()) {
        FacetFiltersForm.filterData.some(filterDataUrl)
          ? FacetFiltersForm.renderDeferredSectionFromCache(filterDataUrl, requestId)
          : FacetFiltersForm.renderDeferredSectionFromFetch(url, requestId);
        return;
      }

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, requestId)
        : FacetFiltersForm.renderSectionFromFetch(url, event, requestId);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static beginRequest() {
    FacetFiltersForm.abortController?.abort();
    FacetFiltersForm.abortController = new AbortController();
    FacetFiltersForm.requestId += 1;
    return FacetFiltersForm.requestId;
  }

  static isLatestRequest(requestId) {
    return requestId === FacetFiltersForm.requestId;
  }

  static renderSectionFromFetch(url, event, requestId) {
    fetch(url, { signal: FacetFiltersForm.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        if (!FacetFiltersForm.isLatestRequest(requestId)) return;
        const parsedResponse = FacetFiltersForm.parseResponse(responseText);
        if (!FacetFiltersForm.isFilterResponse(parsedResponse)) {
          FacetFiltersForm.loadFullPage(url);
          return;
        }
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html: responseText, url }];
        FacetFiltersForm.renderFilters(parsedResponse, event);
        FacetFiltersForm.renderProductGridContainer(parsedResponse);
        FacetFiltersForm.renderProductCount(parsedResponse);
        FacetFiltersForm.initializeScrollAnimations(document.getElementById('ProductGridContainer'));
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || !FacetFiltersForm.isLatestRequest(requestId)) return;
        console.error('Error fetching collection filters:', error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, requestId) {
    if (!FacetFiltersForm.isLatestRequest(requestId)) return;
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    const parsedResponse = FacetFiltersForm.parseResponse(html);
    FacetFiltersForm.renderFilters(parsedResponse, event);
    FacetFiltersForm.renderProductGridContainer(parsedResponse);
    FacetFiltersForm.renderProductCount(parsedResponse);
    FacetFiltersForm.initializeScrollAnimations(document.getElementById('ProductGridContainer'));
  }

  static renderDeferredSectionFromFetch(url, requestId) {
    fetch(url, { signal: FacetFiltersForm.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        if (!FacetFiltersForm.isLatestRequest(requestId)) return;
        const parsedResponse = FacetFiltersForm.parseResponse(responseText);
        if (!FacetFiltersForm.isFilterResponse(parsedResponse)) {
          FacetFiltersForm.loadFullPage(url);
          return;
        }
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html: responseText, url }];
        FacetFiltersForm.renderDeferredControls(parsedResponse);
        FacetFiltersForm.renderProductGridContainer(parsedResponse);
        FacetFiltersForm.renderDeferredDrawer(parsedResponse);
        FacetFiltersForm.hideLoadingSpinners();
        FacetFiltersForm.initializeScrollAnimations(document.getElementById('ProductGridContainer'));
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || !FacetFiltersForm.isLatestRequest(requestId)) return;
        console.error('Error fetching deferred collection filters:', error);
      });
  }

  static renderDeferredSectionFromCache(filterDataUrl, requestId) {
    if (!FacetFiltersForm.isLatestRequest(requestId)) return;
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    const parsedResponse = FacetFiltersForm.parseResponse(html);
    FacetFiltersForm.renderDeferredControls(parsedResponse);
    FacetFiltersForm.renderProductGridContainer(parsedResponse);
    FacetFiltersForm.renderDeferredDrawer(parsedResponse);
    FacetFiltersForm.hideLoadingSpinners();
    FacetFiltersForm.initializeScrollAnimations(document.getElementById('ProductGridContainer'));
  }

  static renderDeferredControls(html) {
    const source = html.getElementById('DrawerFacetsControls');
    const target = document.getElementById('DrawerFacetsControls');

    if (!source || !target) return;

    target.outerHTML = source.outerHTML;
  }

  static renderDeferredDrawer(html) {
    if (
      !window.MyMaraDeferredFacets
      || typeof window.MyMaraDeferredFacets.isDrawerMounted !== 'function'
      || !window.MyMaraDeferredFacets.isDrawerMounted()
      || typeof window.MyMaraDeferredFacets.replaceDrawerFromHtml !== 'function'
    ) {
      return;
    }

    window.MyMaraDeferredFacets.replaceDrawerFromHtml(html);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = html.getElementById('ProductGridContainer').innerHTML;

      let container = document.getElementById('ProductGridContainer');
      let headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10);
      let posTop = container.offsetTop - headerHeight - 50;
      
      window.scrollTo({ top: posTop, behavior: "smooth" });
      
    viewMode();
  }

  static renderProductCount(html) {
    const productCountElement = html.getElementById('ProductCount');
    const productCountDesktopElement = html.getElementById('ProductCountDesktop');
    const productCountMobileElement = html.getElementById('ProductCountMobile');
    const productCountActiveElement = html.getElementById('ProductCountActive');
    const countNewActive = productCountActiveElement ? productCountActiveElement.innerHTML : '0';
    

    const updateContainer = (containerId, count) => {
        const container = document.getElementById(containerId);
        if (container) {
            container.innerHTML = count;
            container.classList.remove('loading');
        }
    };

    if (productCountElement) {
        updateContainer('ProductCount', productCountElement.innerHTML);
    }
    if (productCountDesktopElement) {
        updateContainer('ProductCountDesktop', productCountDesktopElement.innerHTML);
    }
    if (productCountMobileElement) {
        updateContainer('ProductCountMobile', productCountMobileElement.innerHTML);
    }

    const countActive = document.getElementById('ProductCountActive');
    if (countActive) {
        countActive.innerHTML = countNewActive;
    }

    FacetFiltersForm.hideLoadingSpinners();
  }


  static renderFilters(html, event) {
    const facetDetailsElementsFromFetch = html.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          console.log(previousElementClassName, previousElementId);
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(html);
    FacetFiltersForm.renderAdditionalElements(html);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
    reinitializeCustomElements();
    viewMode();
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    //document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    const nextUrl = FacetFiltersForm.buildCollectionUrl(searchParams);
    if (!nextUrl) return;
    history.pushState({ searchParams }, '', nextUrl);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  createSearchParamsFromCheckbox(input) {
    if (!input || !input.name) {
      return '';
    }

    const searchParams = new URLSearchParams(window.location.search);
    const existingValues = searchParams.getAll(input.name);
    searchParams.delete(input.name);

    existingValues.forEach((value) => {
      if (value !== input.value) {
        searchParams.append(input.name, value);
      }
    });

    if (input.checked) {
      searchParams.append(input.name, input.value);
    }

    searchParams.delete('page');
    searchParams.delete('section_id');

    return searchParams.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.target.classList.contains('mobile-facets__checkbox')) {
      const searchParams = this.createSearchParamsFromCheckbox(event.target);
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';
      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm' ) {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.abortController = new AbortController();
FacetFiltersForm.requestId = 0;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
    this.priceSlider();
  }
  connectedCallback(){
    this.priceSlider();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    this.priceSlider();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
  priceSlider() {
    const sliderRange = this.querySelector(".price-slider-range");
    if (!sliderRange) return;

    const [minInput, maxInput] = this.querySelectorAll('input');

    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    const event = new CustomEvent('input');

    const minValue = parseFloat(minInput.value.replace(',', '')) || 0;
    const maxValue = parseFloat(maxInput.value.replace(',', '')) || parseFloat(sliderRange.dataset.max);

    if (sliderRange.noUiSlider) {
      sliderRange.noUiSlider.destroy();
    }
  
    let isSliding = false;
  
    noUiSlider.create(sliderRange, {
      start: [minValue, maxValue],
      connect: true,
      step: 1,
      handleAttributes: [
        { 'aria-label': 'lower' },
        { 'aria-label': 'upper' },
      ],
      range: {
        'min': 0,
        'max': parseFloat(sliderRange.dataset.max)
      }
    });
  
    sliderRange.noUiSlider.on('start', function() {
      isSliding = true;
    });
  
    sliderRange.noUiSlider.on('update', (values) => {
        if (isSliding) {
            minInput.value = values[0];
            maxInput.value = values[1];
        }
    });
  
    sliderRange.noUiSlider.on('end', function() {
      isSliding = false;
      form.querySelector('form').dispatchEvent(event);
    });
  
    const updateSlider = (input, index) => {
      input.addEventListener('change', () => {
          let value = parseFloat(input.value.replace(',', ''));
          if (isNaN(value)) {
              value = 0;
          }
          
          setTimeout(() => {
              const updateValues = [null, null];
              updateValues[index] = value;
              sliderRange.noUiSlider.set(updateValues);
          }, 50);
      });
  };
  
    updateSlider(minInput, 0);
    updateSlider(maxInput, 1);
  }
  
}

customElements.define('price-range', PriceRange);


class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

function getCollectionViewModeStorageKey(viewModeType) {
  if (viewModeType === 'desktop') return 'mymaraCollectionGridColumnsDesktop';
  if (viewModeType === 'mobile') return 'mymaraCollectionGridColumnsMobile';
  return null;
}

function isAllowedCollectionViewMode(viewModeType, viewMode) {
  if (viewModeType === 'desktop') {
    return ['3', '4', '5'].includes(viewMode);
  }

  if (viewModeType === 'mobile') {
    return ['1', '2'].includes(viewMode);
  }

  return false;
}

function getStoredCollectionViewMode(viewModeType) {
  try {
    const storageKey = getCollectionViewModeStorageKey(viewModeType);
    if (!storageKey) return null;

    const storedViewMode = window.localStorage.getItem(storageKey);
    return isAllowedCollectionViewMode(viewModeType, storedViewMode) ? storedViewMode : null;
  } catch (error) {
    return null;
  }
}

function setStoredCollectionViewMode(viewModeType, viewMode) {
  if (!isAllowedCollectionViewMode(viewModeType, viewMode)) return;

  try {
    const storageKey = getCollectionViewModeStorageKey(viewModeType);
    if (!storageKey) return;

    window.localStorage.setItem(storageKey, viewMode);
  } catch (error) {
    // Ignore unavailable storage so the grid control still works normally.
  }
}

function setActiveCollectionViewModeButton(viewModeType, viewMode) {
  const selector = viewModeType === 'mobile' ? '.view-mode__button-mobile' : '.view-mode__button';

  document.querySelectorAll(selector).forEach((button) => {
    button.classList.toggle('active', button.getAttribute('data-view-mode') === viewMode);
  });
}

function applyCollectionViewMode(viewModeType, viewMode, shouldStore = false) {
  const productGrid = document.getElementById('product-grid');
  if (!productGrid || !isAllowedCollectionViewMode(viewModeType, viewMode)) return;

  if (viewModeType === 'mobile') {
    productGrid.classList.remove('grid-cols-1', 'grid-cols-2');
    productGrid.classList.add(`grid-cols-${viewMode}`);
  } else {
    productGrid.classList.remove('lg:grid-cols-3', 'lg:grid-cols-4', 'lg:grid-cols-5');
    productGrid.classList.add(`lg:grid-cols-${viewMode}`);
  }

  setActiveCollectionViewModeButton(viewModeType, viewMode);

  if (shouldStore) {
    setStoredCollectionViewMode(viewModeType, viewMode);
  }
}

function applyStoredCollectionViewModes() {
  if (document.querySelector('.view-mode__button-mobile')) {
    applyCollectionViewMode('mobile', getStoredCollectionViewMode('mobile'));
  }

  if (document.querySelector('.view-mode__button')) {
    applyCollectionViewMode('desktop', getStoredCollectionViewMode('desktop'));
  }
}


function reinitializeCustomElements() {
  const container = document.querySelector('.section-collection-products-grid');
  if (!container) return;

  container.querySelectorAll('collapsible-details').forEach((element) => {
    const isOpen = element.querySelector('details')?.open;
    
    const newElement = element.cloneNode(true);
    element.replaceWith(newElement);

    requestAnimationFrame(() => {
      const newDetails = newElement.querySelector('details');
      if (isOpen) {
        newDetails.open = true;
      }
    });
  });
}

function viewMode() {
  const productGrid = document.getElementById('product-grid');
  if (!productGrid) return;

  const buttons = document.querySelectorAll('.view-mode__button, .view-mode__button-mobile');
  if (buttons.length === 0) return;

  buttons.forEach((button) => {
    if (button.dataset.viewModeBound === 'true') return;
    button.dataset.viewModeBound = 'true';

    button.addEventListener('click', () => {
      const viewMode = button.getAttribute('data-view-mode');
      const viewModeType = button.classList.contains('view-mode__button-mobile') ? 'mobile' : 'desktop';

      applyCollectionViewMode(viewModeType, viewMode, true);
    });
  });

  applyStoredCollectionViewModes();
}
